import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabase/config';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
    const { userData, effectiveTenantId } = useAuth();
    const location = useLocation();

    const isCustomerRoute =
        location.pathname.startsWith('/portal') ||
        location.pathname.startsWith('/check-in') ||
        location.pathname === '/' ||
        ['/pricing', '/about', '/contact', '/legal', '/kvkk', '/privacy', '/sales-agreement', '/integrations', '/whitepaper'].includes(location.pathname);

    // Manage mode locally on the device/browser
    const [mode, setMode] = useState(() => localStorage.getItem('crm_theme_mode') || 'light');

    const [theme, setTheme] = useState({
        primary: '#6f4e37',
        mode: mode
    });

    useEffect(() => {
        // Force light mode for customer routes
        const activeMode = isCustomerRoute ? 'light' : mode;

        // ALWAYS use Ugra Brand Colors (#6f4e37)
        const ugraTheme = {
            primary: '#6f4e37',
            mode: activeMode
        };
        setTheme(ugraTheme);
        applyTheme(ugraTheme);
    }, [mode, isCustomerRoute]);

    const toggleMode = () => {
        const newMode = mode === 'dark' ? 'light' : 'dark';
        setMode(newMode);
        localStorage.setItem('crm_theme_mode', newMode);

        // Apply immediately to avoid flicker/delay
        const root = document.documentElement;
        root.setAttribute('data-theme', newMode);

        // Update local theme state part immediately too
        setTheme(prev => ({ ...prev, mode: newMode }));
    };

    const applyTheme = (themeData) => {
        const root = document.documentElement;
        if (themeData.primary) {
            root.style.setProperty('--primary', themeData.primary);
            // Generate RGB for transparency effects
            const rgb = hexToRgb(themeData.primary);
            if (rgb) {
                root.style.setProperty('--primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);

                // Calculate contrast color (on-primary)
                const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
                const onPrimary = brightness > 155 ? '#000000' : '#ffffff';
                root.style.setProperty('--text-on-primary', onPrimary);
            }
        }
        if (themeData.mode) {
            root.setAttribute('data-theme', themeData.mode);
        }
    };

    const hexToRgb = (hex) => {
        if (!hex) return null;
        let cleanHex = hex.replace('#', '');
        if (cleanHex.length === 3) {
            cleanHex = cleanHex.split('').map(char => char + char).join('');
        }
        const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleMode }}>
            {children}
        </ThemeContext.Provider>
    );
};
